import {
  smartProps,
  useBlockContext,
  useEditor,
  useInspectorControl,
  useSmartProp,
} from "@smartfire/js-block-engine";
import React, { useMemo } from "react";
import { useEffect } from "react";
import { SmartFieldInspector } from "./editor/SmartFieldInspector";
import { FieldType } from "./FieldType";
// import { FieldRegistrationContext } from "./SmartFormContext";
import { SmartFormField } from "./SmartFormField";


export type SmartFieldSetup = {
  smartProp: string;
  type: FieldType;
  defaults?: {
    name?: string;
    required?: boolean;
  };
  editor?: {
    /**
     * Enable the inspector control.
     * @default true
     */
    withInspector?: boolean;

    /**
     * Title for the inspector control section.
     */
    inspectorTitle?: string;
  };
};

export function useSmartField({
  smartProp,
  type,
  defaults,
  editor,
}: SmartFieldSetup) {
  const blockContext = useBlockContext();
  const { isEditing } = useEditor();

  if (!blockContext) {
    throw new Error("We are outside of a block context");
  }

  if (!blockContext.Block.smartProps?.[smartProp]) {
    throw new Error(
      `SmartForm's prop ${smartProp} is not defined in the block`
    );
  }

  if (blockContext.Block.smartProps?.[smartProp]?.type !== "object") {
    throw new Error(`SmartForm's prop ${smartProp} is not an 'object' type`);
  }
  
  const [fieldSetup, updateFieldSetup] = useSmartProp<
    SmartFormField & { "__smartform.version": string, "__smartform.kind": "field" }
  >(smartProp);

  const editorSetup = useMemo(() => ({
    ...editor,
    smartProp
  }), [editor?.inspectorTitle, editor?.withInspector, smartProps]);

  useInspectorControl(
    () =>
      editor?.withInspector === false ? (
        <></>
      ) : (
        <SmartFieldInspector
          editor={editorSetup}
        />
      ),
    [editorSetup]
  );

  useEffect(() => {
    if (isEditing) {
      const blockId = blockContext.attributes.__gutenbergEditor?.clientId;
      if (!blockId) {
        console.error("No block id found");
        return;
      } else if (!fieldSetup) {
        updateFieldSetup({
          blockId,
          smartProp: smartProp,
          name: defaults?.name ?? smartProp,
          type: type,
          required: defaults?.required ?? false,
          "__smartform.version": "1.0.0",
          "__smartform.kind": "field",
        });
      } else if (fieldSetup.blockId !== blockId) {
        updateFieldSetup({
          ...fieldSetup,
          blockId,
        });
      } else if (fieldSetup.type !== type) {
        updateFieldSetup({
          ...fieldSetup,
          type,
        });
      } else if (fieldSetup.smartProp !== smartProp) {
        updateFieldSetup({
          ...fieldSetup,
          smartProp,
        });
      } else if (fieldSetup['__smartform.kind'] !== "field") {
        updateFieldSetup({
          ...fieldSetup,
          "__smartform.kind": "field",
        });
      } else if (fieldSetup['__smartform.version'] !== "1.0.0") {
        updateFieldSetup({
          ...fieldSetup,
          "__smartform.version": "1.0.0",
        });
      }
    }
  }, [fieldSetup, updateFieldSetup, smartProp, type, isEditing]);

  // const { fields, dispatch } = useContext(FieldRegistrationContext);

  // useLayoutEffect(() => {
  //   const field = fields.find((f) => f.smartProp === smartProp && f.blockId === blockContext.attributes.__gutenbergEditor?.clientId);
  //   if (isEditing && fieldSetup) {
  //     if(!isEqual(field, fieldSetup)) {
  //       dispatch({ type: "REGISTER", field: fieldSetup });
  //     }
  //   }
  //   return () => {};
  // }, [fieldSetup, isEditing, dispatch]);

  return {
    field: fieldSetup,
    updateField: updateFieldSetup,
  };
}
